import { ID, RelationshipUpdate, UpdateTask } from '@griply/types';
import { sameSet } from '@griply/utils';

export type BuildTaskLinkWritesOptions = {
  taskId: ID;
  goalIds: ID[];
  lifeAreaIds: ID[];
  currentGoalIds: ID[];
  currentLifeAreaIds: ID[];
  isCompleted?: boolean;
};

export type GoalTaskCountDelta = {
  goalId: ID;
  taskCount: number;
  completedTaskCount: number;
};

export type TaskLinkWrites = {
  taskUpdate: UpdateTask | null;
  relationshipUpdates: RelationshipUpdate[];
  goalCountDeltas: GoalTaskCountDelta[];
};

const buildCountDeltas = (
  goalIds: ID[],
  currentGoalIds: ID[],
  isCompleted: boolean,
): GoalTaskCountDelta[] => {
  const desired = new Set(goalIds);
  const current = new Set(currentGoalIds);
  const completedStep = isCompleted ? 1 : 0;

  return [
    ...goalIds
      .filter((goalId) => !current.has(goalId))
      .map((goalId) => ({
        goalId,
        taskCount: 1,
        completedTaskCount: completedStep,
      })),
    ...currentGoalIds
      .filter((goalId) => !desired.has(goalId))
      .map((goalId) => ({
        goalId,
        taskCount: -1,
        completedTaskCount: isCompleted ? -1 : 0,
      })),
  ];
};

export const buildTaskLinkWrites = ({
  taskId,
  goalIds,
  lifeAreaIds,
  currentGoalIds,
  currentLifeAreaIds,
  isCompleted = false,
}: BuildTaskLinkWritesOptions): TaskLinkWrites => {
  if (
    sameSet(goalIds, currentGoalIds) &&
    sameSet(lifeAreaIds, currentLifeAreaIds)
  ) {
    return { taskUpdate: null, relationshipUpdates: [], goalCountDeltas: [] };
  }

  return {
    taskUpdate: {
      id: taskId,
      goalIds,
      lifeAreaIds,
      goalId: goalIds[0] ?? null,
      lifeAreaId: lifeAreaIds[0] ?? null,
    },
    relationshipUpdates: [],
    goalCountDeltas: buildCountDeltas(goalIds, currentGoalIds, isCompleted),
  };
};
